import { useMemo, useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useDebounce } from 'use-debounce';
import { useUrlState } from './useUrlState';

export type Primitive = string | number | boolean;
type FilterValues<T> = Record<keyof T, Primitive | Primitive[]>;

interface UseUrlFiltersOptions<T extends FilterValues<T>> {
  defaultFilters: T;
  debounce?: number;
}

interface UseUrlFiltersResult<T extends FilterValues<T>> {
  filters: T;
  setFilterField: (field: keyof T, value: Primitive | Primitive[]) => void;
  resetFilters: () => void;
  hasActiveFilters: boolean;
  apiFilters: Record<string, Primitive | Primitive[]>;
}

export const useUrlFilters = <T extends FilterValues<T>>({
  defaultFilters,
  debounce = 300,
}: UseUrlFiltersOptions<T>): UseUrlFiltersResult<T> => {
  const router = useRouter();
  const [urlState, setUrlState] = useUrlState<T>(defaultFilters);

  // Local filters state for immediate UI feedback (before debounce)
  const [filters, setFilters] = useState<T>(urlState);
  const [debouncedFilters] = useDebounce(filters, debounce);

  // Track whether the initial URL hydration has been applied to local filters.
  // This prevents the debounced default values from overwriting URL-hydrated
  // values during the first render cycle.
  const hasSyncedFromUrl = useRef(false);

  // Once the router is ready, useUrlState's hydration effect has run (it shares
  // the same router.isReady gate). Open the local→URL sync gate so subsequent
  // user input can write to the URL — even when the URL was initially bare and
  // the URL→local effect below sees no change to detect.
  useEffect(() => {
    if (router.isReady) {
      hasSyncedFromUrl.current = true;
    }
  }, [router.isReady]);

  // Sync URL state → local filters (handles hydration and external URL changes)
  useEffect(() => {
    const changed = Object.entries(urlState).some(([key, value]) => {
      const currentValue = filters[key as keyof T];
      if (Array.isArray(value) && Array.isArray(currentValue)) {
        if (value.length !== currentValue.length) return true;
        const sortedA = [...(value as string[])].sort();
        const sortedB = [...(currentValue as string[])].sort();
        return sortedA.some((item, i) => item !== sortedB[i]);
      }
      return currentValue !== value;
    });

    if (changed) {
      setFilters(urlState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlState]);

  // Sync debounced local filters → URL state
  // Only runs after we've received at least one sync from URL, preventing
  // defaults from stomping over hydrated URL values.
  useEffect(() => {
    if (!hasSyncedFromUrl.current || !router.isReady) return;

    const hasChanges = Object.entries(debouncedFilters).some(([key, value]) => {
      const urlValue = urlState[key as keyof T];
      if (Array.isArray(value) && Array.isArray(urlValue)) {
        if (value.length !== urlValue.length) return true;
        const sortedA = [...(value as string[])].sort();
        const sortedB = [...(urlValue as string[])].sort();
        return sortedA.some((item, i) => item !== sortedB[i]);
      }
      return value !== urlValue;
    });

    if (hasChanges) {
      setUrlState(prev => ({ ...prev, ...debouncedFilters }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFilters]);

  const setFilterField = (field: keyof T, value: Primitive | Primitive[]) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const resetFilters = () => {
    setUrlState({ ...defaultFilters });
    setFilters(defaultFilters);
  };

  const hasActiveFilters = Object.keys(defaultFilters).some(key => {
    const filterKey = key as keyof T;
    const current = filters[filterKey];
    const initial = defaultFilters[filterKey];

    if (Array.isArray(current) && Array.isArray(initial)) {
      if (current.length !== initial.length) return true;
      const sortedCurrent = [...current].sort();
      const sortedInitial = [...initial].sort();
      return sortedCurrent.some((item, i) => item !== sortedInitial[i]);
    }

    return current !== initial && current !== '' && current !== null && current !== undefined;
  });

  const apiFilters = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(debouncedFilters).filter(([_, v]) => (Array.isArray(v) ? v.length > 0 : v !== '' && v != null)),
      ) as Record<string, Primitive | Primitive[]>,
    [debouncedFilters],
  );

  return {
    filters,
    setFilterField,
    resetFilters,
    hasActiveFilters,
    apiFilters,
  };
};
